import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';

// ==========================================
// ICONOGRAFÍA VECTORIAL EN TRAZO (STROKE OUTLINE)
// ==========================================

const StrokeMic: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const StrokeMicOff: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const StrokeVideo: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="3" ry="3" />
  </svg>
);

const StrokeVideoOff: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const StrokeVolume: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
);

const StrokeVolumeOff: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

const StrokePhoneHangup: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.8 19.8 0 0 1-3.12-8.68A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" transform="rotate(135 12 12)" />
  </svg>
);

const StrokeSparkles: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.4 5.6L20 10l-5.6 2.4L12 18l-2.4-5.6L4 10l5.6-2.4z" />
    <path d="M19 14l1 2.5L22.5 17.5 20 18.5 19 21l-1-2.5-2.5-1 2.5-1z" />
  </svg>
);

const StrokeMaximize: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
  </svg>
);

const StrokeMinimize: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 14h6m0 0v6m0-6L3 21m17-7h-6m0 0v6m0-6l7 7M4 10h6m0 0V4m0 6L3 3m17 7h-6m0 0V4m0 6l7-7" />
  </svg>
);

const StrokeFlipCamera: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0-4.42-3.58-8-8-8s-8 3.58-8 8c0 2.03.76 3.87 2 5.28L4 18h6v-6l-2.4 2.4C6.6 13.2 6 11.7 6 10c0-3.31 2.69-6 6-6s6 2.69 6 6c0 1.66-.68 3.16-1.76 4.24l1.42 1.42C19.16 14.16 20 12.18 20 10z" />
  </svg>
);

// ==========================================
// DEFINICIÓN DE FILTROS EN TIEMPO REAL
// ==========================================

const COLOR_FILTERS = [
  { id: 'none', label: 'Natural', css: 'none', preview: '#38bdf8' },
  { id: 'golden', label: 'Cálido Dorado', css: 'sepia(0.28) saturate(1.35) contrast(1.05) brightness(1.04)', preview: '#f59e0b' },
  { id: 'cold', label: 'Cian Frío', css: 'hue-rotate(185deg) saturate(1.15) contrast(1.05)', preview: '#06b6d4' },
  { id: 'noir', label: 'Noir B&W', css: 'grayscale(1) contrast(1.25) brightness(0.96)', preview: '#64748b' },
  { id: 'cyber', label: 'Cyberpunk', css: 'saturate(2.2) contrast(1.3) hue-rotate(290deg)', preview: '#ec4899' },
  { id: 'vintage', label: 'Vintage 90s', css: 'sepia(0.55) contrast(1.12) brightness(0.92) saturate(1.2)', preview: '#d97706' },
  { id: 'beauty', label: 'Soft Glow', css: 'brightness(1.08) contrast(0.98) saturate(1.18)', preview: '#a855f7' },
  { id: 'sunset', label: 'Atardecer', css: 'hue-rotate(330deg) saturate(1.45) contrast(1.12)', preview: '#ea580c' },
];

const BG_EFFECTS = [
  { id: 'none', label: 'Original', desc: 'Fondo estándar' },
  { id: 'blur', label: 'Desenfoque Bokeh', desc: 'Modo retrato con desenfoque de ambiente' },
  { id: 'vignette', label: 'Viñeta Cine', desc: 'Sombra perimetral cinematográfica' },
  { id: 'glass', label: 'Oficina Zoik Glass', desc: 'Fondo ejecutivo de cristal translúcido' },
  { id: 'neon_frame', label: 'Marco Neón Cyber', desc: 'Resplandor dinámico en los bordes' }
];

const FACE_FILTERS = [
  { id: 'none', label: 'Sin accesorio', icon: '❌' },
  { id: 'sunglasses', label: 'Gafas de Sol', icon: '🕶️' },
  { id: 'crown', label: 'Corona Dorada', icon: '👑' },
  { id: 'cat_ears', label: 'Orejas de Gato', icon: '🐱' },
  { id: 'mustache', label: 'Bigote & Sombrero', icon: '🥸' },
  { id: 'sparkles', label: 'Lluvia de Destellos', icon: '✨' },
  { id: 'cyber_visor', label: 'Visor Holográfico', icon: '🤖' }
];

interface CallContextType {
  callState: 'idle' | 'calling' | 'incoming' | 'connected';
  activeCall: any;
  callDuration: number;
  isMuted: boolean;
  isCamOff: boolean;
  isSpeakerMuted: boolean;
  layoutMode: 'widget' | 'expanded' | 'fullscreen';
  activeColorFilter: string;
  activeBgEffect: string;
  activeFaceFilter: string;
  startCall: (otherUser: any, isVideo: boolean) => Promise<void>;
  acceptIncomingCall: () => Promise<void>;
  rejectIncomingCall: () => void;
  endActiveCall: () => void;
  toggleMute: () => void;
  toggleCam: () => void;
  toggleSpeaker: () => void;
  setLayoutMode: (mode: 'widget' | 'expanded' | 'fullscreen') => void;
  setActiveColorFilter: (f: string) => void;
  setActiveBgEffect: (f: string) => void;
  setActiveFaceFilter: (f: string) => void;
}

const CallContext = createContext<CallContextType | null>(null);

// MOTOR DE TIMBRE CLÁSICO ESTILO SKYPE (WEB AUDIO API)
let ringInterval: any = null;
let ringAudioCtx: AudioContext | null = null;

const SKYPE_NOTES = [
  { f: 587.33, t: 0.00, d: 0.15, v: 0.16 }, // Re5 (D5)
  { f: 739.99, t: 0.16, d: 0.15, v: 0.17 }, // Fa#5 (F#5)
  { f: 880.00, t: 0.32, d: 0.15, v: 0.18 }, // La5 (A5)
  { f: 987.77, t: 0.48, d: 0.15, v: 0.19 }, // Si5 (B5)
  { f: 1174.66, t: 0.64, d: 0.26, v: 0.21 }, // Re6 (D6)
  { f: 987.77, t: 0.92, d: 0.18, v: 0.18 }, // Si5 (B5)
  { f: 880.00, t: 1.12, d: 0.20, v: 0.17 }, // La5 (A5)
  { f: 739.99, t: 1.34, d: 0.40, v: 0.17 }, // Fa#5 (F#5)

  { f: 659.25, t: 1.85, d: 0.15, v: 0.16 }, // Mi5 (E5)
  { f: 739.99, t: 2.01, d: 0.15, v: 0.17 }, // Fa#5 (F#5)
  { f: 880.00, t: 2.17, d: 0.24, v: 0.19 }, // La5 (A5)
  { f: 587.33, t: 2.45, d: 0.65, v: 0.20 }, // Re5 (D5)
];

const stopRingtoneSound = () => {
  if (ringInterval) {
    clearInterval(ringInterval);
    ringInterval = null;
  }
  if (ringAudioCtx) {
    try { ringAudioCtx.close(); } catch {}
    ringAudioCtx = null;
  }
};

const startRingtoneSound = (isIncoming: boolean) => {
  stopRingtoneSound();

  if (isIncoming) {
    const playSkypeRingtone = () => {
      try {
        if (ringAudioCtx) {
          try { ringAudioCtx.close(); } catch {}
        }
        ringAudioCtx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
        const ctx = ringAudioCtx;
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume();

        SKYPE_NOTES.forEach(n => {
          const noteTime = ctx.currentTime + n.t;

          const osc1 = ctx.createOscillator();
          const gain1 = ctx.createGain();
          osc1.type = 'sine';
          osc1.frequency.setValueAtTime(n.f, noteTime);

          gain1.gain.setValueAtTime(0.0001, noteTime);
          gain1.gain.linearRampToValueAtTime(n.v, noteTime + 0.006);
          gain1.gain.exponentialRampToValueAtTime(0.0001, noteTime + n.d + 0.22);

          osc1.connect(gain1);
          gain1.connect(ctx.destination);

          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.type = 'triangle';
          osc2.frequency.setValueAtTime(n.f * 2, noteTime);

          gain2.gain.setValueAtTime(0.0001, noteTime);
          gain2.gain.linearRampToValueAtTime(n.v * 0.22, noteTime + 0.005);
          gain2.gain.exponentialRampToValueAtTime(0.0001, noteTime + (n.d * 0.65));

          osc2.connect(gain2);
          gain2.connect(ctx.destination);

          osc1.start(noteTime);
          osc1.stop(noteTime + n.d + 0.25);
          osc2.start(noteTime);
          osc2.stop(noteTime + n.d + 0.25);
        });
      } catch {}
    };

    playSkypeRingtone();
    ringInterval = setInterval(playSkypeRingtone, 4200);
  } else {
    const playSkypeOutgoing = () => {
      try {
        if (ringAudioCtx) {
          try { ringAudioCtx.close(); } catch {}
        }
        ringAudioCtx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
        const ctx = ringAudioCtx;
        if (!ctx) return;
        if (ctx.state === 'suspended') ctx.resume();

        const t0 = ctx.currentTime;

        const o1 = ctx.createOscillator();
        const g1 = ctx.createGain();
        o1.type = 'sine';
        o1.frequency.setValueAtTime(587.33, t0);
        o1.frequency.exponentialRampToValueAtTime(739.99, t0 + 0.07);
        g1.gain.setValueAtTime(0.0001, t0);
        g1.gain.linearRampToValueAtTime(0.16, t0 + 0.01);
        g1.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
        o1.connect(g1);
        g1.connect(ctx.destination);
        o1.start(t0);
        o1.stop(t0 + 0.18);

        const t1 = t0 + 0.18;
        const o2 = ctx.createOscillator();
        const g2 = ctx.createGain();
        o2.type = 'sine';
        o2.frequency.setValueAtTime(880.00, t1);
        o2.frequency.exponentialRampToValueAtTime(1174.66, t1 + 0.08);
        g2.gain.setValueAtTime(0.0001, t1);
        g2.gain.linearRampToValueAtTime(0.18, t1 + 0.01);
        g2.gain.exponentialRampToValueAtTime(0.0001, t1 + 0.28);
        o2.connect(g2);
        g2.connect(ctx.destination);
        o2.start(t1);
        o2.stop(t1 + 0.30);
      } catch {}
    };

    playSkypeOutgoing();
    ringInterval = setInterval(playSkypeOutgoing, 2400);
  }
};

const ini = (s: string = '') => s.split(' ').map(n => n[0] || '').join('').slice(0, 2).toUpperCase();
const fmtDuration = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const RTC_CONFIG: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:31.97.167.176:3478' },
    {
      urls: [
        'turn:31.97.167.176:3478',
        'turn:31.97.167.176:3478?transport=tcp'
      ],
      username: 'zoikuser',
      credential: 'zoikpass2026'
    },
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' }
  ],
  iceCandidatePoolSize: 10
};

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const myId = user?.id || (user as any)?.userId || '';

  const [callState, setCallState] = useState<'idle' | 'calling' | 'incoming' | 'connected'>('idle');
  const [activeCall, setActiveCall] = useState<any>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isCamOff, setIsCamOff] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);
  const [layoutMode, setLayoutMode] = useState<'widget' | 'expanded' | 'fullscreen'>('widget');

  // Estados de Filtros y Efectos en Tiempo Real
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [filtersTab, setFiltersTab] = useState<'color' | 'bg' | 'face'>('color');
  const [activeColorFilter, setActiveColorFilter] = useState('none');
  const [activeBgEffect, setActiveBgEffect] = useState('none');
  const [activeFaceFilter, setActiveFaceFilter] = useState('none');

  // Streams de video local y remoto
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const iceCandidateQueueRef = useRef<any[]>([]);
  const callTimerRef = useRef<any>(null);
  const activeCallRef = useRef<any>(null);

  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);

  // Detener timbres al conectar o cerrar
  useEffect(() => {
    if (callState === 'connected' || callState === 'idle') {
      stopRingtoneSound();
    }
  }, [callState]);

  // Callback Ref para asignación instantánea al montar nodo de video local
  const attachLocalVideo = useCallback((node: HTMLVideoElement | null) => {
    localVideoRef.current = node;
    if (node && localStreamRef.current) {
      node.srcObject = localStreamRef.current;
      node.muted = true;
      node.playsInline = true;
      node.play().catch(() => {});
    }
  }, []);

  // Callback Ref para asignación instantánea al montar nodo de video remoto
  const attachRemoteVideo = useCallback((node: HTMLVideoElement | null) => {
    remoteVideoRef.current = node;
    if (node && remoteStreamRef.current) {
      node.srcObject = remoteStreamRef.current;
      node.setAttribute('playsinline', 'true');
      node.setAttribute('webkit-playsinline', 'true');
      node.muted = true;
      node.play().catch(() => {});
    }
  }, []);

  const attachRemoteAudio = useCallback((node: HTMLAudioElement | null) => {
    remoteAudioRef.current = node;
    if (node && remoteStreamRef.current) {
      node.srcObject = remoteStreamRef.current;
      node.muted = isSpeakerMuted;
      node.play().catch(() => {});
    }
  }, [isSpeakerMuted]);

  // Sincronizador reactivo de localVideoRef cuando cambia localStream o estado
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
      localVideoRef.current.muted = true;
      localVideoRef.current.playsInline = true;
      localVideoRef.current.play().catch(() => {});
    }
  }, [localStream, callState, layoutMode, isCamOff]);

  // Sincronizador reactivo de remoteVideoRef y remoteAudioRef cuando cambia remoteStream
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current.setAttribute('playsinline', 'true');
      remoteVideoRef.current.setAttribute('webkit-playsinline', 'true');
      remoteVideoRef.current.muted = true;
      remoteVideoRef.current.play().catch(() => {});
    }
    if (remoteAudioRef.current && remoteStream) {
      remoteAudioRef.current.srcObject = remoteStream;
      remoteAudioRef.current.muted = isSpeakerMuted;
      remoteAudioRef.current.play().catch(() => {});
    }
  }, [remoteStream, callState, layoutMode, isSpeakerMuted]);

  // Conexión Global de Socket
  useEffect(() => {
    if (!myId) return;

    const socket = io('https://zoikanalyst.com', { path: '/socket.io', transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('user:join', myId);
    });

    socket.on('call:incoming', (data) => {
      stopRingtoneSound();
      setActiveCall({
        isVideo: Boolean(data.isVideo),
        peerId: data.callerId,
        peerName: data.callerName,
        peerAvatar: data.callerAvatar,
        isCaller: false
      });
      setCallState('incoming');
      startRingtoneSound(true);
    });

    socket.on('call:accepted', async (data) => {
      stopRingtoneSound();
      setCallState('connected');
      toast.success('¡Llamada conectada!');
      setCallDuration(0);
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      callTimerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);

      const pc = pcRef.current;
      const targetUserId = activeCallRef.current?.peerId || data?.responderId;
      if (pc && targetUserId) {
        try {
          const offer = await pc.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true
          });
          await pc.setLocalDescription(offer);
          socket.emit('call:signal', {
            toUserId: targetUserId,
            fromUserId: myId,
            signal: { type: 'offer', sdp: offer.sdp }
          });
        } catch (e) { console.error('Error creando oferta SDP:', e); }
      }
    });

    socket.on('call:rejected', () => {
      stopRingtoneSound();
      toast.error('Llamada rechazada o no contestada');
      cleanupCall();
    });

    socket.on('call:signal', async (data) => {
      if (!data?.signal) return;
      const signal = data.signal;

      if (signal.type === 'candidate' && signal.candidate) {
        const pc = pcRef.current;
        if (pc && pc.remoteDescription && pc.remoteDescription.type) {
          try {
            await pc.addIceCandidate(signal.candidate);
          } catch (e) {
            console.warn('addIceCandidate error:', e);
          }
        } else {
          iceCandidateQueueRef.current.push(signal.candidate);
        }
        return;
      }

      const pc = pcRef.current;
      if (!pc) return;

      try {
        if (signal.type === 'offer') {
          const sdpText = typeof signal.sdp === 'string' ? signal.sdp : (signal.sdp?.sdp || signal.sdp);
          await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: sdpText }));

          while (iceCandidateQueueRef.current.length > 0) {
            const cand = iceCandidateQueueRef.current.shift();
            if (cand) {
              try { await pc.addIceCandidate(cand); } catch (e) { console.warn('ICE drain error:', e); }
            }
          }

          const answer = await pc.createAnswer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true
          });
          await pc.setLocalDescription(answer);
          
          const recipientId = data.fromUserId || activeCallRef.current?.peerId;
          socket.emit('call:signal', {
            toUserId: recipientId,
            fromUserId: myId,
            signal: { type: 'answer', sdp: answer.sdp }
          });
        } else if (signal.type === 'answer') {
          const sdpText = typeof signal.sdp === 'string' ? signal.sdp : (signal.sdp?.sdp || signal.sdp);
          await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: sdpText }));

          while (iceCandidateQueueRef.current.length > 0) {
            const cand = iceCandidateQueueRef.current.shift();
            if (cand) {
              try { await pc.addIceCandidate(cand); } catch (e) { console.warn('ICE drain error:', e); }
            }
          }
        }
      } catch (err) { console.error('Error procesando señal WebRTC:', err); }
    });

    socket.on('call:ended', () => {
      stopRingtoneSound();
      toast('Llamada finalizada');
      cleanupCall();
    });

    return () => {
      socket.disconnect();
      cleanupCall();
    };
  }, [myId]);

  // Limpieza y Cierre Forzoso de Llamada
  const cleanupCall = () => {
    stopRingtoneSound();
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => {
        try { t.stop(); } catch {}
      });
      localStreamRef.current = null;
    }
    remoteStreamRef.current = null;
    iceCandidateQueueRef.current = [];
    setLocalStream(null);
    setRemoteStream(null);
    if (pcRef.current) {
      try { pcRef.current.close(); } catch {}
      pcRef.current = null;
    }
    setCallState('idle');
    setActiveCall(null);
    setCallDuration(0);
    setIsMuted(false);
    setIsCamOff(false);
    setIsSpeakerMuted(false);
    setLayoutMode('widget');
    setShowFiltersPanel(false);
  };

  // Helper para solicitar permisos con fallback progresivo
  const getMediaStream = async (isVideo: boolean): Promise<MediaStream | null> => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Tu navegador no soporta llamadas WebRTC');
      return null;
    }

    if (isVideo) {
      try {
        return await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { width: { ideal: 1280 }, height: { ideal: 720 } }
        });
      } catch (e1) {
        try {
          return await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        } catch (e2) {
          try {
            return await navigator.mediaDevices.getUserMedia({ video: true });
          } catch (e3) {
            try {
              return await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch (e4) {
              return null;
            }
          }
        }
      }
    } else {
      try {
        return await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        return null;
      }
    }
  };

  // Iniciar Llamada
  const startCall = async (otherUser: any, isVideo: boolean) => {
    if (!otherUser || !otherUser.id) {
      toast.error('Selecciona un usuario para llamar');
      return;
    }

    try {
      const stream = await getMediaStream(isVideo);
      localStreamRef.current = stream;
      setLocalStream(stream);

      const pc = new RTCPeerConnection(RTC_CONFIG);

      if (stream) {
        stream.getTracks().forEach(track => {
          track.enabled = true;
          pc.addTrack(track, stream!);
        });
      } else {
        pc.addTransceiver('audio', { direction: 'recvonly' });
        if (isVideo) pc.addTransceiver('video', { direction: 'recvonly' });
      }

      pc.ontrack = (event) => {
        let stream: MediaStream;
        if (event.streams && event.streams[0]) {
          stream = event.streams[0];
        } else {
          if (!remoteStreamRef.current) {
            remoteStreamRef.current = new MediaStream();
          }
          remoteStreamRef.current.addTrack(event.track);
          stream = remoteStreamRef.current;
        }

        remoteStreamRef.current = stream;
        setRemoteStream(new MediaStream(stream.getTracks()));

        event.track.onunmute = () => {
          if (remoteVideoRef.current) {
            if (remoteVideoRef.current.srcObject !== remoteStreamRef.current) {
              remoteVideoRef.current.srcObject = remoteStreamRef.current;
            }
            remoteVideoRef.current.play().catch(() => {});
          }
        };

        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = stream;
          remoteVideoRef.current.play().catch(() => {});
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && socketRef.current) {
          const targetId = activeCallRef.current?.peerId || otherUser.id;
          socketRef.current.emit('call:signal', {
            toUserId: targetId,
            fromUserId: myId,
            signal: { type: 'candidate', candidate: event.candidate.toJSON ? event.candidate.toJSON() : event.candidate }
          });
        }
      };

      pc.onconnectionstatechange = () => {
        console.log('📡 [WebRTC] Estado conexión:', pc.connectionState);
      };

      pcRef.current = pc;

      const callInfo = {
        isVideo: Boolean(isVideo),
        peerId: otherUser.id,
        peerName: `${otherUser.firstName || 'Usuario'} ${otherUser.lastName || ''}`.trim(),
        peerAvatar: otherUser.avatar,
        isCaller: true
      };

      setActiveCall(callInfo);
      activeCallRef.current = callInfo;
      setCallState('calling');
      startRingtoneSound(false);

      socketRef.current?.emit('call:initiate', {
        toUserId: otherUser.id,
        callerId: myId,
        callerName: `${user?.firstName || 'Usuario'} ${user?.lastName || ''}`.trim(),
        callerAvatar: (user as any)?.avatar,
        isVideo: Boolean(isVideo)
      });
    } catch {
      toast.error('Error al iniciar llamada');
    }
  };

  // Contestar Llamada
  const acceptIncomingCall = async () => {
    stopRingtoneSound();
    if (!activeCall) return;

    try {
      const isVideo = Boolean(activeCall.isVideo);
      const stream = await getMediaStream(isVideo);

      localStreamRef.current = stream;
      setLocalStream(stream);

      const pc = new RTCPeerConnection(RTC_CONFIG);

      if (stream) {
        stream.getTracks().forEach(track => {
          track.enabled = true;
          pc.addTrack(track, stream!);
        });
      } else {
        pc.addTransceiver('audio', { direction: 'recvonly' });
        if (isVideo) pc.addTransceiver('video', { direction: 'recvonly' });
      }

      pc.ontrack = (event) => {
        let stream: MediaStream;
        if (event.streams && event.streams[0]) {
          stream = event.streams[0];
        } else {
          if (!remoteStreamRef.current) {
            remoteStreamRef.current = new MediaStream();
          }
          remoteStreamRef.current.addTrack(event.track);
          stream = remoteStreamRef.current;
        }

        remoteStreamRef.current = stream;
        setRemoteStream(new MediaStream(stream.getTracks()));

        event.track.onunmute = () => {
          if (remoteVideoRef.current) {
            if (remoteVideoRef.current.srcObject !== remoteStreamRef.current) {
              remoteVideoRef.current.srcObject = remoteStreamRef.current;
            }
            remoteVideoRef.current.play().catch(() => {});
          }
        };

        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = stream;
          remoteVideoRef.current.play().catch(() => {});
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && socketRef.current) {
          const targetId = activeCallRef.current?.peerId || activeCall.peerId;
          socketRef.current.emit('call:signal', {
            toUserId: targetId,
            fromUserId: myId,
            signal: { type: 'candidate', candidate: event.candidate.toJSON ? event.candidate.toJSON() : event.candidate }
          });
        }
      };

      pc.onconnectionstatechange = () => {
        console.log('📡 [WebRTC] Estado conexión:', pc.connectionState);
      };

      pcRef.current = pc;

      socketRef.current?.emit('call:accept', {
        callerId: activeCall.peerId,
        responderId: myId
      });

      setCallState('connected');
      setCallDuration(0);
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      callTimerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
    } catch {
      setCallState('connected');
    }
  };

  // Rechazar Llamada Entrante
  const rejectIncomingCall = () => {
    stopRingtoneSound();
    if (activeCall && socketRef.current) {
      socketRef.current.emit('call:reject', { callerId: activeCall.peerId });
    }
    cleanupCall();
  };

  // Colgar / Finalizar Llamada Activa
  const endActiveCall = () => {
    stopRingtoneSound();
    if (activeCall && socketRef.current) {
      socketRef.current.emit('call:end', { toUserId: activeCall.peerId });
    }
    cleanupCall();
  };

  // Silenciar / Activar Micrófono
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
        toast(audioTrack.enabled ? '🎙️ Micrófono activado' : '🔇 Micrófono silenciado');
        return;
      }
    }
    setIsMuted(v => !v);
  };

  // Silenciar / Activar Altavoz (Audio Remoto)
  const toggleSpeaker = () => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.muted = !remoteVideoRef.current.muted;
      setIsSpeakerMuted(remoteVideoRef.current.muted);
      toast(remoteVideoRef.current.muted ? '🔈 Altavoz silenciado' : '🔊 Altavoz activado');
    }
  };

  // Encender / Apagar Cámara
  const toggleCam = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsCamOff(!videoTrack.enabled);
        toast(videoTrack.enabled ? '📹 Cámara encendida' : '🚫 Cámara apagada');
      }
    }
  };

  // Obtener CSS de filtro activo
  const currentColorFilterCss = COLOR_FILTERS.find(f => f.id === activeColorFilter)?.css || 'none';

  return (
    <CallContext.Provider value={{
      callState, activeCall, callDuration, isMuted, isCamOff, isSpeakerMuted, layoutMode,
      activeColorFilter, activeBgEffect, activeFaceFilter,
      startCall, acceptIncomingCall, rejectIncomingCall, endActiveCall,
      toggleMute, toggleCam, toggleSpeaker, setLayoutMode,
      setActiveColorFilter, setActiveBgEffect, setActiveFaceFilter
    }}>
      {children}

      {/* POPUP DE LLAMADA ENTRANTE GLOBAL (ESTILO APPLE LIQUID GLASS) */}
      {callState === 'incoming' && activeCall && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          width: '330px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '22px',
          border: '1.5px solid rgba(0, 112, 243, 0.4)',
          boxShadow: '0 24px 60px rgba(0, 40, 100, 0.25)',
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px',
          fontFamily: '"Montserrat", sans-serif',
          animation: 'zoikSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
        }}>
          <div style={{ width: '68px', height: '68px', borderRadius: '50%', background: 'linear-gradient(135deg, #0070f3, #0056d6)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 900, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0, 112, 243, 0.35)' }}>
            {activeCall.peerAvatar ? <img src={activeCall.peerAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(activeCall.peerName)}
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: 900, color: '#0f2a4a' }}>{activeCall.peerName}</div>
            <div style={{ fontSize: '12px', color: '#0070f3', fontWeight: 700, marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              {activeCall.isVideo ? <StrokeVideo size={14} color="#0070f3" /> : <StrokeMic size={14} color="#0070f3" />}
              {activeCall.isVideo ? 'Videollamada Entrante...' : 'Llamada de Voz Entrante...'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '4px' }}>
            <button
              onClick={rejectIncomingCall}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '14px',
                border: 'none',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: 'white',
                fontWeight: 800,
                fontSize: '12.5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 6px 18px rgba(239, 68, 68, 0.35)'
              }}
            >
              <StrokePhoneHangup size={16} color="#ffffff" />
              Rechazar
            </button>
            <button
              onClick={acceptIncomingCall}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '14px',
                border: 'none',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                fontWeight: 800,
                fontSize: '12.5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 6px 18px rgba(16, 185, 129, 0.35)'
              }}
            >
              {activeCall.isVideo ? <StrokeVideo size={16} color="#ffffff" /> : <StrokeMic size={16} color="#ffffff" />}
              Contestar
            </button>
          </div>
        </div>
      )}

      {/* ESTUDIO DE VIDEOLLAMADA EN VIVO (WIDGET FLOTANTE, EXPANDIDO O PANTALLA COMPLETA) */}
      {(callState === 'calling' || callState === 'connected') && activeCall && (
        <div style={{
          position: 'fixed',
          ...(layoutMode === 'fullscreen' ? {
            inset: 0,
            width: '100vw',
            height: '100dvh',
            borderRadius: 0,
            border: 'none'
          } : layoutMode === 'expanded' ? {
            bottom: '24px',
            right: '24px',
            width: '880px',
            height: '560px',
            maxHeight: '92vh',
            maxWidth: '95vw',
            borderRadius: '26px',
            border: '1.5px solid rgba(0, 112, 243, 0.5)'
          } : {
            bottom: '24px',
            right: '24px',
            width: activeCall.isVideo ? '380px' : '320px',
            height: activeCall.isVideo ? '280px' : 'auto',
            maxHeight: '90vh',
            maxWidth: '94vw',
            borderRadius: '22px',
            border: '1.5px solid rgba(0, 112, 243, 0.5)'
          }),
          background: '#07162c',
          boxShadow: '0 24px 70px rgba(0, 0, 0, 0.75)',
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          fontFamily: '"Montserrat", sans-serif'
        }}>

          {/* CABECERA DE LA VIDEOLLAMADA */}
          <div style={{
            padding: layoutMode === 'fullscreen' ? '14px 24px' : '10px 16px',
            background: 'linear-gradient(180deg, rgba(7, 22, 44, 0.85) 0%, rgba(7, 22, 44, 0) 100%)',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 60,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pointerEvents: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                padding: '6px',
                borderRadius: '10px',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {activeCall.isVideo ? <StrokeVideo size={16} color="#38bdf8" /> : <StrokeMic size={16} color="#38bdf8" />}
              </div>
              <div>
                <div style={{ fontSize: layoutMode === 'fullscreen' ? '15px' : '13px', fontWeight: 900, color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px' }}>
                  {activeCall.peerName}
                </div>
                <div style={{ fontSize: '10.5px', color: callState === 'calling' ? '#fbbf24' : '#34d399', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: callState === 'calling' ? '#fbbf24' : '#34d399' }} />
                  {callState === 'calling' ? 'Llamando...' : `En vivo • ${fmtDuration(callDuration)}`}
                </div>
              </div>
            </div>

            {/* BOTONES DE VENTANA Y MODOS */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Botón de Filtros / Efectos */}
              {activeCall.isVideo && (
                <button
                  onClick={() => setShowFiltersPanel(v => !v)}
                  title="Filtros y Efectos en Vivo"
                  style={{
                    background: showFiltersPanel ? 'rgba(0, 112, 243, 0.8)' : 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#ffffff',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '11.5px',
                    fontWeight: 800,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <StrokeSparkles size={14} color="#ffffff" />
                  <span>Filtros</span>
                </button>
              )}

              {/* Botón Pantalla Completa */}
              <button
                onClick={() => {
                  if (layoutMode === 'fullscreen') setLayoutMode('widget');
                  else setLayoutMode('fullscreen');
                }}
                title={layoutMode === 'fullscreen' ? 'Salir de Pantalla Completa' : 'Pantalla Completa'}
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  padding: '7px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {layoutMode === 'fullscreen' ? <StrokeMinimize size={16} color="#ffffff" /> : <StrokeMaximize size={16} color="#ffffff" />}
              </button>
            </div>
          </div>

          {/* CUERPO PRINCIPAL DE VIDEO */}
          <div style={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: activeBgEffect === 'glass' ? 'linear-gradient(135deg, #0b1e36 0%, #030a14 100%)' : '#030a14',
            overflow: 'hidden'
          }}>

            {activeCall.isVideo ? (
              <>
                {/* VIDEO REMOTO (PANTALLA PRINCIPAL) */}
                <video
                  ref={attachRemoteVideo}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: 1
                  }}
                />
                <audio ref={attachRemoteAudio} autoPlay playsInline style={{ display: 'none' }} />

                {/* EFECTO DE FONDO: VIÑETA CINEMATOGRÁFICA */}
                {activeBgEffect === 'vignette' && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    boxShadow: 'inset 0 0 100px rgba(0,0,0,0.85)',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }} />
                )}

                {/* EFECTO DE FONDO: MARCO NEÓN GAMER */}
                {activeBgEffect === 'neon_frame' && (
                  <div style={{
                    position: 'absolute',
                    inset: '6px',
                    border: '2px solid #00f0ff',
                    boxShadow: '0 0 25px rgba(0, 240, 255, 0.6), inset 0 0 25px rgba(0, 240, 255, 0.3)',
                    borderRadius: '18px',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }} />
                )}

                {/* PLACEHOLDER MIENTRAS SE CONECTA LA OTRA PERSONA */}
                {!remoteStream && (
                  <div style={{ position: 'relative', zIndex: 3, padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: layoutMode === 'fullscreen' ? '100px' : '72px', height: layoutMode === 'fullscreen' ? '100px' : '72px', borderRadius: '50%', background: 'linear-gradient(135deg, #0070f3, #0056d6)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: 900, overflow: 'hidden', boxShadow: '0 0 0 10px rgba(0,112,243,0.25)' }}>
                      {activeCall.peerAvatar ? <img src={activeCall.peerAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(activeCall.peerName)}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 800 }}>
                      {callState === 'calling' ? 'Llamando a ' + activeCall.peerName + '...' : `Conectando video con ${activeCall.peerName}...`}
                    </div>
                  </div>
                )}

                {/* VIDEO LOCAL PIP (AUTO-VISTA CON FILTROS EN TIEMPO REAL) */}
                <div style={{
                  position: 'absolute',
                  bottom: layoutMode === 'fullscreen' ? '110px' : '80px',
                  right: '16px',
                  width: layoutMode === 'fullscreen' ? '240px' : layoutMode === 'expanded' ? '190px' : '120px',
                  height: layoutMode === 'fullscreen' ? '160px' : layoutMode === 'expanded' ? '130px' : '82px',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '2px solid rgba(0, 112, 243, 0.9)',
                  background: '#07162c',
                  boxShadow: '0 12px 36px rgba(0,0,0,0.7)',
                  zIndex: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.25s ease'
                }}>
                  <video
                    ref={attachLocalVideo}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: 'scaleX(-1)', // Efecto espejo
                      filter: currentColorFilterCss,
                      display: isCamOff ? 'none' : 'block'
                    }}
                  />

                  {/* SUPERPOSICIÓN DE FILTROS DE ROSTRO / ACCESORIOS */}
                  {!isCamOff && activeFaceFilter !== 'none' && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      zIndex: 42
                    }}>
                      {activeFaceFilter === 'sunglasses' && (
                        <span style={{ fontSize: layoutMode === 'fullscreen' ? '46px' : '28px', marginTop: '-10px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))' }}>
                          🕶️
                        </span>
                      )}
                      {activeFaceFilter === 'crown' && (
                        <span style={{ fontSize: layoutMode === 'fullscreen' ? '42px' : '26px', marginTop: '-42px', filter: 'drop-shadow(0 4px 10px rgba(251,191,36,0.6))' }}>
                          👑
                        </span>
                      )}
                      {activeFaceFilter === 'cat_ears' && (
                        <span style={{ fontSize: layoutMode === 'fullscreen' ? '46px' : '28px', marginTop: '-45px', filter: 'drop-shadow(0 4px 8px rgba(236,72,153,0.5))' }}>
                          🐱
                        </span>
                      )}
                      {activeFaceFilter === 'mustache' && (
                        <span style={{ fontSize: layoutMode === 'fullscreen' ? '42px' : '26px', marginTop: '14px', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.6))' }}>
                          🥸
                        </span>
                      )}
                      {activeFaceFilter === 'sparkles' && (
                        <span style={{ fontSize: layoutMode === 'fullscreen' ? '48px' : '30px', filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.9))' }}>
                          ✨
                        </span>
                      )}
                      {activeFaceFilter === 'cyber_visor' && (
                        <span style={{ fontSize: layoutMode === 'fullscreen' ? '44px' : '28px', marginTop: '-8px', filter: 'drop-shadow(0 0 14px rgba(0,240,255,0.8))' }}>
                          🤖
                        </span>
                      )}
                    </div>
                  )}

                  {isCamOff && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', color: 'rgba(255,255,255,0.7)' }}>
                      <StrokeVideoOff size={18} color="rgba(255,255,255,0.7)" />
                      <span style={{ fontSize: '9.5px', fontWeight: 800 }}>Cámara off</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* LLAMADA DE VOZ HD */
              <div style={{ padding: '36px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #0070f3, #0056d6)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', fontWeight: 900, boxShadow: '0 0 0 12px rgba(0, 112, 243, 0.25)', overflow: 'hidden' }}>
                  {activeCall.peerAvatar ? <img src={activeCall.peerAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ini(activeCall.peerName)}
                </div>
                <div style={{ color: '#ffffff', fontSize: '13px', fontWeight: 800, textAlign: 'center' }}>
                  {callState === 'calling' ? 'Esperando respuesta...' : 'Llamada de voz HD activa'}
                </div>
                <video ref={attachRemoteVideo} autoPlay playsInline style={{ display: 'none' }} />
              </div>
            )}
          </div>

          {/* PANEL DE FILTROS FLOTANTE (DRAWER LIQUID GLASS) */}
          {showFiltersPanel && activeCall.isVideo && (
            <div style={{
              position: 'absolute',
              bottom: layoutMode === 'fullscreen' ? '96px' : '72px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '92%',
              maxWidth: '540px',
              background: 'rgba(15, 42, 74, 0.88)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              borderRadius: '20px',
              padding: '14px 16px',
              border: '1.5px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.65)',
              zIndex: 70,
              animation: 'zoikSlideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1)'
            }}>
              {/* Pestañas de Filtros */}
              <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '10px', marginBottom: '12px' }}>
                <button
                  onClick={() => setFiltersTab('color')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: filtersTab === 'color' ? '#0070f3' : 'rgba(255,255,255,0.08)',
                    color: '#ffffff',
                    fontSize: '11.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  🎨 Tono & Color
                </button>
                <button
                  onClick={() => setFiltersTab('bg')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: filtersTab === 'bg' ? '#0070f3' : 'rgba(255,255,255,0.08)',
                    color: '#ffffff',
                    fontSize: '11.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  🖼️ Fondo & Luz
                </button>
                <button
                  onClick={() => setFiltersTab('face')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: filtersTab === 'face' ? '#0070f3' : 'rgba(255,255,255,0.08)',
                    color: '#ffffff',
                    fontSize: '11.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  🎭 Rostro & AR
                </button>
              </div>

              {/* Contenido Pestaña 1: Tono y Color */}
              {filtersTab === 'color' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  {COLOR_FILTERS.map(f => {
                    const isSel = activeColorFilter === f.id;
                    return (
                      <button
                        key={f.id}
                        onClick={() => setActiveColorFilter(f.id)}
                        style={{
                          padding: '8px 6px',
                          borderRadius: '12px',
                          border: isSel ? '2px solid #0070f3' : '1px solid rgba(255,255,255,0.12)',
                          background: isSel ? 'rgba(0, 112, 243, 0.25)' : 'rgba(255,255,255,0.06)',
                          color: '#ffffff',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: f.preview, border: '1.5px solid rgba(255,255,255,0.8)' }} />
                        <span style={{ fontSize: '10px', fontWeight: isSel ? 900 : 600 }}>{f.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Contenido Pestaña 2: Fondo y Luz */}
              {filtersTab === 'bg' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {BG_EFFECTS.map(b => {
                    const isSel = activeBgEffect === b.id;
                    return (
                      <button
                        key={b.id}
                        onClick={() => setActiveBgEffect(b.id)}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '12px',
                          border: isSel ? '2px solid #0070f3' : '1px solid rgba(255,255,255,0.12)',
                          background: isSel ? 'rgba(0, 112, 243, 0.25)' : 'rgba(255,255,255,0.06)',
                          color: '#ffffff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          textAlign: 'left'
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '11.5px', fontWeight: 800 }}>{b.label}</div>
                          <div style={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.65)' }}>{b.desc}</div>
                        </div>
                        {isSel && <span style={{ color: '#0070f3', fontWeight: 900 }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Contenido Pestaña 3: Rostro & AR */}
              {filtersTab === 'face' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  {FACE_FILTERS.map(fc => {
                    const isSel = activeFaceFilter === fc.id;
                    return (
                      <button
                        key={fc.id}
                        onClick={() => setActiveFaceFilter(fc.id)}
                        style={{
                          padding: '8px 6px',
                          borderRadius: '12px',
                          border: isSel ? '2px solid #0070f3' : '1px solid rgba(255,255,255,0.12)',
                          background: isSel ? 'rgba(0, 112, 243, 0.25)' : 'rgba(255,255,255,0.06)',
                          color: '#ffffff',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <span style={{ fontSize: '20px' }}>{fc.icon}</span>
                        <span style={{ fontSize: '9.5px', fontWeight: isSel ? 900 : 600, textAlign: 'center' }}>{fc.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* BARRA DE HERRAMIENTAS INFERIOR FLOTANTE (ESTILO APPLE DOCK / LIQUID GLASS) */}
          <div style={{
            padding: layoutMode === 'fullscreen' ? '18px 24px' : '12px 16px',
            background: 'linear-gradient(0deg, rgba(7, 22, 44, 0.92) 0%, rgba(7, 22, 44, 0) 100%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: layoutMode === 'fullscreen' ? '18px' : '12px',
            zIndex: 60,
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0
          }}>
            {/* Micrófono On / Off */}
            <button
              onClick={toggleMute}
              title={isMuted ? 'Activar Micrófono' : 'Silenciar Micrófono'}
              style={{
                width: layoutMode === 'fullscreen' ? '50px' : '42px',
                height: layoutMode === 'fullscreen' ? '50px' : '42px',
                borderRadius: '50%',
                border: isMuted ? 'none' : '1px solid rgba(255,255,255,0.25)',
                background: isMuted ? '#ef4444' : 'rgba(255,255,255,0.18)',
                backdropFilter: 'blur(12px)',
                color: '#ffffff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
                boxShadow: isMuted ? '0 4px 14px rgba(239, 68, 68, 0.4)' : 'none'
              }}
            >
              {isMuted ? <StrokeMicOff size={layoutMode === 'fullscreen' ? 22 : 18} color="#ffffff" /> : <StrokeMic size={layoutMode === 'fullscreen' ? 22 : 18} color="#ffffff" />}
            </button>

            {/* Altavoz On / Off */}
            <button
              onClick={toggleSpeaker}
              title={isSpeakerMuted ? 'Activar Altavoz' : 'Silenciar Altavoz'}
              style={{
                width: layoutMode === 'fullscreen' ? '50px' : '42px',
                height: layoutMode === 'fullscreen' ? '50px' : '42px',
                borderRadius: '50%',
                border: isSpeakerMuted ? 'none' : '1px solid rgba(255,255,255,0.25)',
                background: isSpeakerMuted ? '#ef4444' : 'rgba(255,255,255,0.18)',
                backdropFilter: 'blur(12px)',
                color: '#ffffff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
                boxShadow: isSpeakerMuted ? '0 4px 14px rgba(239, 68, 68, 0.4)' : 'none'
              }}
            >
              {isSpeakerMuted ? <StrokeVolumeOff size={layoutMode === 'fullscreen' ? 22 : 18} color="#ffffff" /> : <StrokeVolume size={layoutMode === 'fullscreen' ? 22 : 18} color="#ffffff" />}
            </button>

            {/* Cámara On / Off (Solo videollamadas) */}
            {activeCall.isVideo && (
              <button
                onClick={toggleCam}
                title={isCamOff ? 'Encender Cámara' : 'Apagar Cámara'}
                style={{
                  width: layoutMode === 'fullscreen' ? '50px' : '42px',
                  height: layoutMode === 'fullscreen' ? '50px' : '42px',
                  borderRadius: '50%',
                  border: isCamOff ? 'none' : '1px solid rgba(255,255,255,0.25)',
                  background: isCamOff ? '#ef4444' : 'rgba(255,255,255,0.18)',
                  backdropFilter: 'blur(12px)',
                  color: '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                  boxShadow: isCamOff ? '0 4px 14px rgba(239, 68, 68, 0.4)' : 'none'
                }}
              >
                {isCamOff ? <StrokeVideoOff size={layoutMode === 'fullscreen' ? 22 : 18} color="#ffffff" /> : <StrokeVideo size={layoutMode === 'fullscreen' ? 22 : 18} color="#ffffff" />}
              </button>
            )}

            {/* Botón Colgar / Finalizar */}
            <button
              onClick={endActiveCall}
              title="Finalizar Llamada"
              style={{
                width: layoutMode === 'fullscreen' ? '56px' : '46px',
                height: layoutMode === 'fullscreen' ? '56px' : '46px',
                borderRadius: '50%',
                border: 'none',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#ffffff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(239, 68, 68, 0.5)',
                transition: 'all 0.15s ease'
              }}
            >
              <StrokePhoneHangup size={layoutMode === 'fullscreen' ? 24 : 20} color="#ffffff" />
            </button>
          </div>
        </div>
      )}
    </CallContext.Provider>
  );
};

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) throw new Error('useCall debe usarse dentro de CallProvider');
  return context;
};

export default CallContext;
